import { NCNode } from '../../../../src/commonTypes';

export type NodeOriginKey =
	| 'web'
	| 'pubmed'
	| 'gdelt'
	| 'pdf'
	| 'vision'
	| 'haiku'
	| 'tagline'
	| 'brainstorm'
	| 'common'
	| 'persona'
	| 'audience'
	| 'motivations'
	| 'link'
	| 'onboarding-llm'
	| 'onboarding-kg';

export type NodeOrigin = {
	key: NodeOriginKey;
	label: string;
};

// Display labels per origin key. Kept short so the badge stays compact.
const ORIGIN_LABELS: Record<NodeOriginKey, string> = {
	web: 'web',
	pubmed: 'pubmed',
	gdelt: 'gdelt',
	pdf: 'PDF',
	vision: 'focus',
	haiku: 'haiku',
	tagline: 'tagline',
	brainstorm: 'brainstorm',
	common: 'common idea',
	persona: 'persona',
	audience: 'audience',
	motivations: 'motivations',
	link: 'link',
	'onboarding-llm': 'LLM seed',
	'onboarding-kg': 'KG seed',
};

// Topic prefixes that map directly to an origin key. Search results write
// `source:web|pubmed|gdelt`, generated phrase nodes write `source:<task>`.
const TOPIC_SOURCE_MAP: Record<string, NodeOriginKey> = {
	web: 'web',
	pubmed: 'pubmed',
	gdelt: 'gdelt',
	pdf: 'pdf',
	vision: 'vision',
	haiku: 'haiku',
	'ai-haiku': 'haiku',
	proverb: 'tagline',
	tagline: 'tagline',
	brainstorm: 'brainstorm',
	common: 'common',
	persona: 'persona',
	audience: 'audience',
	motivations: 'motivations',
	'onboarding-llm': 'onboarding-llm',
	'onboarding-kg': 'onboarding-kg',
};

const findLensTopic = (topics: string[] | undefined): string | null => {
	if (!topics?.length) return null;
	const lensTopic = topics.find((topic) => {
		const lower = topic.toLowerCase();
		return lower.startsWith('lens:') && !lower.startsWith('lens-id:');
	});
	return lensTopic ? lensTopic.slice(lensTopic.indexOf(':') + 1).trim() : null;
};

const findOnboardingLensTopic = (topics: string[] | undefined): string | null => {
	if (!topics?.length) return null;
	const lensTopic = topics.find((topic) => topic.toLowerCase().startsWith('lens:'));
	return lensTopic ? lensTopic.slice(lensTopic.indexOf(':') + 1) : null;
};

const findSourceTopic = (topics: string[] | undefined): NodeOriginKey | null => {
	if (!topics || !topics.length) {
		return null;
	}
	for (const topic of topics) {
		if (!topic) continue;
		const lower = topic.toLowerCase();
		const colonIdx = lower.indexOf(':');
		if (colonIdx <= 0) continue;
		const prefix = lower.slice(0, colonIdx);
		const value = lower.slice(colonIdx + 1);
		if (prefix === 'source' || prefix === 'task' || prefix === 'origin') {
			const mapped = TOPIC_SOURCE_MAP[value];
			if (mapped) return mapped;
		}
		if (prefix === 'pdf') {
			return 'pdf';
		}
	}
	return null;
};

const formatLensBadgeLabel = (lensId: string): string => lensId.replace(/_/g, ' ');

const matchesAny = (haystack: string, needles: string[]): boolean => {
	const lower = haystack.toLowerCase();
	return needles.some((needle) => lower.includes(needle));
};

const deriveFromParentLabel = (parentLabel: string): NodeOriginKey | null => {
	const trimmed = parentLabel.trim();
	if (!trimmed) return null;
	if (matchesAny(trimmed, ['haiku'])) return 'haiku';
	if (matchesAny(trimmed, ['proverb', 'tagline'])) return 'tagline';
	if (matchesAny(trimmed, ['brainstorm'])) return 'brainstorm';
	if (matchesAny(trimmed, ['common idea', 'common ideas'])) return 'common';
	if (matchesAny(trimmed, ['persona'])) return 'persona';
	if (matchesAny(trimmed, ['audience'])) return 'audience';
	if (matchesAny(trimmed, ['motivation'])) return 'motivations';
	return null;
};

/**
 * Derives an origin descriptor for a node from existing fields.
 *
 * Priority: explicit topics (`source:web`, etc) > parent.label heuristics >
 * fallback to a generic "link" badge when only an `href` is present. Returns
 * `null` when no origin can be confidently inferred.
 */
export const getNodeOrigin = (node: NCNode): NodeOrigin | null => {
	const fromTopics = findSourceTopic(node.topics);
	if (fromTopics) {
		const researchLens = findLensTopic(node.topics);
		if (researchLens && (fromTopics === 'web' || fromTopics === 'pubmed' || fromTopics === 'gdelt')) {
			return { key: fromTopics, label: researchLens };
		}
		if (fromTopics === 'onboarding-llm' || fromTopics === 'onboarding-kg') {
			const lensId = findOnboardingLensTopic(node.topics);
			if (lensId) {
				return { key: fromTopics, label: formatLensBadgeLabel(lensId) };
			}
		}
		return { key: fromTopics, label: ORIGIN_LABELS[fromTopics] };
	}

	if (node.parent?.label) {
		const fromParent = deriveFromParentLabel(node.parent.label);
		if (fromParent) {
			return { key: fromParent, label: ORIGIN_LABELS[fromParent] };
		}
	}

	if (node.href) {
		return { key: 'link', label: ORIGIN_LABELS.link };
	}

	return null;
};

export const getNodeOriginLabel = (node: NCNode): string | null =>
	getNodeOrigin(node)?.label ?? null;

const normalizeOriginLabel = (value: string): string =>
	value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Avoid rendering an origin pill that merely repeats the node's visible title. */
export const nodeOriginRepeatsLabel = (
	origin: NodeOrigin,
	nodeLabel?: string | null
): boolean => {
	if (!nodeLabel?.trim()) return false;
	return normalizeOriginLabel(origin.label) === normalizeOriginLabel(nodeLabel);
};

/** Board focus / project-vision anchor seeded at the centre of a phrase cluster. */
export const isBoardAnchorNode = (node: NCNode): boolean =>
	Boolean(node.topics?.some((topic) => topic.toLowerCase() === 'source:vision'));
